import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  CloseSyncStatus,
} from "@/api/runs";
import type {
  MergeProgress,
  RunDetail,
  RunSnapshot,
  SystemView,
} from "@/api/types";

/**
 * Single SSE channel for the whole authed app — one EventSource per tab.
 *
 * Replaces the legacy polling on /api/stats, /api/close/sync/status,
 * /api/runs/{id}, and /api/runs/{id}/snapshots. The backend pushes events
 * when underlying state changes (run lifecycle, worker progress, close
 * sync, snapshots) and a 10 s sampler for system metrics that have no
 * natural change event.
 *
 * The browser auto-reconnects on drop using the server's `retry: 3000`
 * directive, so there's no manual reconnect logic here.
 */

export interface SystemSample {
  worker_count: number;
  dlq_size: number;
  queue_size: number;
  system: SystemView;
}

export interface RunsSample {
  recent_runs: RunDetail[];
  totals: {
    domains: number;
    extracted: number;
    skipped: number;
    close_links: number;
    indexed: number;
  };
}

interface SnapshotEvent {
  run_id: string;
  sample: RunSnapshot;
}

export interface MergeProgressEvent {
  run_id: string;
  source: string;
  progress: MergeProgress;
}

export interface VerificationProgressEvent {
  check_id: string;
  run_id: string;
  // Null on the terminal frame; integer 1..total otherwise.
  completed: number | null;
  total: number | null;
  // Null on the terminal frame; per-sample verdict + verification chips
  // otherwise. The SPA stores the latest event keyed by check_id so the
  // panel can apply per-sample state as samples complete.
  latest_outcome: {
    sample_id: string;
    verdict: "pass" | "fail" | "error";
    matched_tags: string[];
    evidence: Array<Record<string, unknown>>;
    word_hits: Record<string, number> | null;
    unreachable_reason: string | null;
    harness_error: string | null;
  } | null;
}

export interface CheckAuthoringProgressEvent {
  check_id: string;
  run_id: string;
  status: "running" | "succeeded" | "failed";
  // One agent step (plan / tool_call / message / proposal / error), or null
  // on the terminal frame.
  step: {
    at: string;
    kind: "plan" | "tool_call" | "message" | "proposal" | "error";
    summary: string;
    data: Record<string, unknown>;
  } | null;
  // True on the terminal frame; the panel then refetches GET /authoring for
  // the final run-doc (proposed_check, token usage).
  done: boolean;
}

interface Ctx {
  system: SystemSample | null;
  runs: RunsSample | null;
  closeSync: CloseSyncStatus | null;
  snapshotsByRun: Map<string, RunSnapshot[]>;
  // `inverted_index_progress` lives durably on the run doc, but the live
  // stream gets sub-second updates between `runs` payload rebuilds, so we
  // maintain a per-run-id Map populated from `merge_progress` events.
  mergeProgressByRun: Map<string, MergeProgressEvent>;
  // Latest `verification_progress` event per check_id. The corpus panel
  // on `/checks/:id` reads this to render the progress bar + patch
  // per-sample verdict pills in real time. Terminal frame (latest_outcome
  // null) tells the panel to drop the progress bar and refetch GET
  // /verification for the final run-doc state.
  verificationByCheck: Map<string, VerificationProgressEvent>;
  // Latest `check_authoring_progress` event per check_id — drives the AI
  // "Improve with AI" panel's live step log + spinner on `/checks/:id`.
  authoringByCheck: Map<string, CheckAuthoringProgressEvent>;
}

const EventStreamCtx = createContext<Ctx | null>(null);

export function EventStreamProvider({ children }: { children: React.ReactNode }) {
  const [system, setSystem] = useState<SystemSample | null>(null);
  const [runs, setRuns] = useState<RunsSample | null>(null);
  const [closeSync, setCloseSync] = useState<CloseSyncStatus | null>(null);
  const [snapshotsByRun, setSnapshotsByRun] = useState<Map<string, RunSnapshot[]>>(
    () => new Map(),
  );
  const [mergeProgressByRun, setMergeProgressByRun] = useState<
    Map<string, MergeProgressEvent>
  >(() => new Map());
  const [verificationByCheck, setVerificationByCheck] = useState<
    Map<string, VerificationProgressEvent>
  >(() => new Map());
  const [authoringByCheck, setAuthoringByCheck] = useState<
    Map<string, CheckAuthoringProgressEvent>
  >(() => new Map());
  const errorLoggedRef = useRef(false);
  const hasOpenedRef = useRef(false);
  const qc = useQueryClient();

  useEffect(() => {
    const es = new EventSource("/api/events", { withCredentials: true });

    // After a sleep gap or transient drop, the live SSE tail picks up new
    // events but the historical-snapshot bootstrap query is stale, so the
    // chart draws a straight line across the gap. Refetching on reconnect
    // and tab-visible pulls in the samples the API daemon was writing all
    // along (every 60 s, regardless of any browser being open).
    const refetchHistory = () => {
      qc.invalidateQueries({ queryKey: ["snapshots"] });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") refetchHistory();
    };
    document.addEventListener("visibilitychange", onVisible);

    es.addEventListener("system", (e) => {
      try { setSystem(JSON.parse((e as MessageEvent).data)); }
      catch (err) { console.warn("bad system event", err); }
    });
    es.addEventListener("runs", (e) => {
      try { setRuns(JSON.parse((e as MessageEvent).data)); }
      catch (err) { console.warn("bad runs event", err); }
    });
    es.addEventListener("close_sync", (e) => {
      try { setCloseSync(JSON.parse((e as MessageEvent).data)); }
      catch (err) { console.warn("bad close_sync event", err); }
    });
    es.addEventListener("snapshot", (e) => {
      try {
        const m: SnapshotEvent = JSON.parse((e as MessageEvent).data);
        setSnapshotsByRun((prev) => {
          const next = new Map(prev);
          const existing = next.get(m.run_id) ?? [];
          // Append; dedupe by `at` in case the server replays on reconnect.
          if (existing.some((s) => s.at === m.sample.at)) return prev;
          next.set(m.run_id, [...existing, m.sample]);
          return next;
        });
      } catch (err) { console.warn("bad snapshot event", err); }
    });
    es.addEventListener("merge_progress", (e) => {
      try {
        const m: MergeProgressEvent = JSON.parse((e as MessageEvent).data);
        setMergeProgressByRun((prev) => {
          const next = new Map(prev);
          // Terminal phases linger for ~5 s so the SPA can flash a checkmark
          // before the row disappears; the BackgroundActivityCard filters
          // them out itself based on `updated_at` age.
          next.set(m.run_id, m);
          return next;
        });
      } catch (err) { console.warn("bad merge_progress event", err); }
    });
    es.addEventListener("verification_progress", (e) => {
      try {
        const m: VerificationProgressEvent = JSON.parse((e as MessageEvent).data);
        setVerificationByCheck((prev) => {
          const next = new Map(prev);
          next.set(m.check_id, m);
          return next;
        });
        // On the terminal frame, refetch GET /verification so the panel
        // picks up the final run-doc state (status=succeeded/failed,
        // any outcomes the SSE may have missed during a reconnect blip).
        if (m.latest_outcome === null) {
          qc.invalidateQueries({ queryKey: ["verification", m.check_id] });
        }
      } catch (err) { console.warn("bad verification_progress event", err); }
    });
    es.addEventListener("check_authoring_progress", (e) => {
      try {
        const m: CheckAuthoringProgressEvent = JSON.parse((e as MessageEvent).data);
        setAuthoringByCheck((prev) => {
          const next = new Map(prev);
          next.set(m.check_id, m);
          return next;
        });
        // Terminal frame: refetch GET /authoring for the final run-doc
        // (proposed_check, token usage, terminal status).
        if (m.done) {
          qc.invalidateQueries({ queryKey: ["authoring", m.check_id] });
        }
      } catch (err) { console.warn("bad check_authoring_progress event", err); }
    });

    es.onerror = () => {
      // Browser auto-reconnects per server `retry:` directive. Log once
      // so we don't spam the console during transient disconnects.
      if (!errorLoggedRef.current) {
        errorLoggedRef.current = true;
        console.info("event stream interrupted; browser will reconnect");
      }
    };
    es.onopen = () => {
      errorLoggedRef.current = false;
      // Skip the very first open — the bootstrap query is already firing.
      if (hasOpenedRef.current) refetchHistory();
      hasOpenedRef.current = true;
    };

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      es.close();
    };
  }, [qc]);

  const value = useMemo<Ctx>(
    () => ({
      system, runs, closeSync, snapshotsByRun, mergeProgressByRun,
      verificationByCheck, authoringByCheck,
    }),
    [system, runs, closeSync, snapshotsByRun, mergeProgressByRun,
     verificationByCheck, authoringByCheck],
  );

  return (
    <EventStreamCtx.Provider value={value}>{children}</EventStreamCtx.Provider>
  );
}

function useCtx(): Ctx {
  const v = useContext(EventStreamCtx);
  if (!v) {
    throw new Error("useEventStream hook used outside EventStreamProvider");
  }
  return v;
}

export const useSystem = () => useCtx().system;
export const useRuns = () => useCtx().runs;
export const useCloseSync = () => useCtx().closeSync;

/**
 * Per-run-id Map of the latest `merge_progress` SSE event. Consumers
 * (BackgroundActivityCard) filter to currently-active phases and decide
 * when to drop terminal-phase rows.
 */
export const useMergeProgress = (): Map<string, MergeProgressEvent> =>
  useCtx().mergeProgressByRun;

/**
 * Latest `verification_progress` event for the given check. None until
 * the user clicks "Run verification". The panel uses this for the
 * progress bar + per-sample verdict patching in real time.
 */
export const useVerificationProgress = (
  checkId: string | undefined,
): VerificationProgressEvent | undefined => {
  const map = useCtx().verificationByCheck;
  if (!checkId) return undefined;
  return map.get(checkId);
};

/**
 * Latest `check_authoring_progress` event for the given check. None until
 * the user clicks "Improve with AI". The AI panel uses this for the live
 * step log + running spinner.
 */
export const useCheckAuthoringProgress = (
  checkId: string | undefined,
): CheckAuthoringProgressEvent | undefined => {
  const map = useCtx().authoringByCheck;
  if (!checkId) return undefined;
  return map.get(checkId);
};

export function useRunFromList(runId: string | undefined): RunDetail | undefined {
  const runs = useRuns();
  if (!runId || !runs) return undefined;
  return runs.recent_runs.find((r) => r.id === runId);
}

export function useRunSnapshots(runId: string | undefined): RunSnapshot[] {
  const map = useCtx().snapshotsByRun;
  if (!runId) return [];
  return map.get(runId) ?? [];
}
